/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
 * document.addEventListener(ThemeEvents.variantUpdate, (e) => { console.log(e.detail.variant) });
 */
import { fetchConfig } from '@theme/utilities';

const DISCOUNT_KIT_CART_CHANGED_EVENT = 'discount_kit:cart_changed';
const CART_ENDPOINT = '/cart.js';
const DISCOUNT_KIT_SYNC_SOURCE = 'discount-kit-sync';
const DISCOUNT_KIT_SYNC_START_EVENT = 'discount-kit:sync:start';
const DISCOUNT_KIT_SYNC_END_EVENT = 'discount-kit:sync:end';
const DISCOUNT_KIT_FALLBACK_DELAY_MS = 1200;
const DISCOUNT_KIT_SYNC_MIN_VISIBLE_MS = 400;
const DISCOUNT_KIT_SYNC_ROOT_ATTRIBUTE = 'data-discount-kit-syncing';
const SECTIONS_API_MAX = 5;

let latestDiscountKitSyncRequestId = 0;
let nextDiscountKitSyncCycleId = 0;
let activeDiscountKitSyncCycleId = 0;
let activeDiscountKitSyncStartedAt = 0;
let discountKitFallbackTimeoutId = null;
let discountKitSyncEndTimeoutId = null;

export class ThemeEvents {
  /** @static @constant {string} Event triggered when a variant is selected */
  static variantSelected = 'variant:selected';
  /** @static @constant {string} Event triggered when a variant is changed */
  static variantUpdate = 'variant:update';
  /** @static @constant {string} Event triggered when the cart items or quantities are updated */
  static cartUpdate = 'cart:update';
  /** @static @constant {string} Event triggered when a cart update fails */
  static cartError = 'cart:error';
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  static mediaStartedPlaying = 'media:started-playing';
  // Event triggered when quantity-selector value is changed
  static quantitySelectorUpdate = 'quantity-selector:update';
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  static megaMenuHover = 'megaMenu:hover';
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  static zoomMediaSelected = 'zoom-media:selected';
  /** @static @constant {string} Event triggered when a discount is applied */
  static discountUpdate = 'discount:update';
  /** @static @constant {string} Event triggered when changing collection filters */
  static FilterUpdate = 'filter:update';
}

/**
 * Event fired when a variant is selected
 * @extends {Event}
 */
export class VariantSelectedEvent extends Event {
  /**
   * Creates a new VariantSelectedEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The option value id
   */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class VariantUpdateEvent extends Event {
  /**
   * Creates a new VariantUpdateEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   * @param {boolean} resource.available - Whether the variant is available
   * @param {boolean} resource.inventory_management - Whether the variant has inventory management
   * @param {string} [resource.sku] - The SKU of the variant
   * @param {Object} [resource.featured_media] - The featured media of the variant
   * @param {string} [resource.featured_media.id] - The id of the featured media
   * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
   * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {Document} data.html - The new document fragment for the variant
   * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
   * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
   * @param {string} data.newProduct.id - The id of the new product
   * @param {string} data.newProduct.url - The url of the new product
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct,
      },
    };
  }
}

/**
 * Event class for cart additions
 * @extends {Event}
 */
export class CartAddEvent extends Event {
  /**
   * Creates a new CartAddEvent
   * @param {Object} [resource] - The new cart object
   * @param {string} [sourceId] - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was added
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }

  static eventName = ThemeEvents.cartUpdate;
}

/**
 * Event class for cart updates
 * @extends {Event}
 */
export class CartUpdateEvent extends Event {
  /**
   * Creates a new CartUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was updated
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }
}

function setDiscountKitSyncUiState(isSyncing) {
  document.documentElement.toggleAttribute(DISCOUNT_KIT_SYNC_ROOT_ATTRIBUTE, isSyncing);

  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (!(component instanceof HTMLElement)) return;

    if (isSyncing) {
      component.dataset.cartSyncing = 'true';
      component.setAttribute('aria-busy', 'true');
    } else {
      component.removeAttribute('data-cart-syncing');
      component.setAttribute('aria-busy', 'false');
    }

    component.querySelectorAll('[ref="cartSyncStatus"]').forEach((cartSyncStatus) => {
      if (!(cartSyncStatus instanceof HTMLElement)) return;
      cartSyncStatus.textContent = isSyncing ? cartSyncStatus.dataset.message || '' : '';
    });
  });
}

function startDiscountKitSyncCycle(itemCount) {
  if (activeDiscountKitSyncCycleId !== 0) {
    endDiscountKitSyncCycle(activeDiscountKitSyncCycleId, 'replaced', { immediate: true });
  }

  const cycleId = ++nextDiscountKitSyncCycleId;
  activeDiscountKitSyncCycleId = cycleId;
  activeDiscountKitSyncStartedAt = Date.now();
  setDiscountKitSyncUiState(true);

  document.dispatchEvent(
    new CustomEvent(DISCOUNT_KIT_SYNC_START_EVENT, {
      detail: { cycleId, source: 'theme', itemCount },
    })
  );

  return cycleId;
}

function endDiscountKitSyncCycle(cycleId, reason, { immediate = false } = {}) {
  if (cycleId !== activeDiscountKitSyncCycleId) return;

  if (discountKitFallbackTimeoutId) {
    window.clearTimeout(discountKitFallbackTimeoutId);
    discountKitFallbackTimeoutId = null;
  }

  if (immediate) {
    finalizeDiscountKitSyncCycle(cycleId, reason);
    return;
  }

  const elapsed = Date.now() - activeDiscountKitSyncStartedAt;
  const remainingVisibleTime = Math.max(DISCOUNT_KIT_SYNC_MIN_VISIBLE_MS - elapsed, 0);

  clearDiscountKitSyncEndTimeout();

  if (remainingVisibleTime === 0) {
    finalizeDiscountKitSyncCycle(cycleId, reason);
    return;
  }

  discountKitSyncEndTimeoutId = window.setTimeout(() => {
    finalizeDiscountKitSyncCycle(cycleId, reason);
  }, remainingVisibleTime);
}

function finalizeDiscountKitSyncCycle(cycleId, reason) {
  if (cycleId !== activeDiscountKitSyncCycleId) return;

  clearDiscountKitSyncEndTimeout();
  activeDiscountKitSyncCycleId = 0;
  activeDiscountKitSyncStartedAt = 0;
  setDiscountKitSyncUiState(false);

  document.dispatchEvent(
    new CustomEvent(DISCOUNT_KIT_SYNC_END_EVENT, {
      detail: { cycleId, reason },
    })
  );
}

function clearDiscountKitFallbackTimeout() {
  if (!discountKitFallbackTimeoutId) return;
  window.clearTimeout(discountKitFallbackTimeoutId);
  discountKitFallbackTimeoutId = null;
}

function clearDiscountKitSyncEndTimeout() {
  if (!discountKitSyncEndTimeoutId) return;
  window.clearTimeout(discountKitSyncEndTimeoutId);
  discountKitSyncEndTimeoutId = null;
}

/**
 * Collects unique section IDs from every cart-items-component on the page.
 * Capped at SECTIONS_API_MAX (5) to stay within Shopify's Sections Rendering
 * API limit. In practice this theme has 1-2 cart-items-component instances per
 * page (cart drawer in header, cart page on /cart), well under the cap.
 *
 * @returns {string[]} Section IDs to render alongside a cart fetch.
 */
function collectCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids).slice(0, SECTIONS_API_MAX);
}

/**
 * Fetches the latest cart and, when cart-items-component instances are mounted,
 * the rendered HTML for their sections in a single combined request. Falls back
 * to a plain GET /cart.js when no cart UI is on the page (e.g. password page).
 *
 * @returns {Promise<Object|null>} Cart JSON (with optional `sections` map) or null on failure.
 */
async function fetchLatestCartWithSections() {
  const signal =
    typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
      ? AbortSignal.timeout(5000)
      : undefined;

  const sectionIds = collectCartSectionIds();

  try {
    if (sectionIds.length === 0) {
      // No cart UI mounted — nothing to render. Plain JSON read preserves today's behavior.
      const response = await fetch(CART_ENDPOINT, { credentials: 'same-origin', signal });
      if (!response.ok) return null;
      return await response.json();
    }

    // Combined request: returns cart JSON + rendered section HTML in one round-trip.
    // `updates: {}` is an explicit no-op mutation — see spec for why this shape over a
    // bare section-only POST.
    const response = await fetch(Theme.routes.cart_update_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({
          updates: {},
          sections: sectionIds.join(','),
          'sections_url': window.location.pathname,
        }),
      }),
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (_) {
    return null;
  }
}

function getCartFromDiscountKitEvent(event) {
  if (!(event instanceof CustomEvent)) return null;
  const detail = event.detail;
  if (!detail || typeof detail !== 'object') return null;
  if ('item_count' in detail) return detail;
  if ('cart' in detail && detail.cart && typeof detail.cart === 'object') return detail.cart;
  return null;
}

async function syncCartForCycle(cycleId, cartFromEvent, reason) {
  if (cycleId !== activeDiscountKitSyncCycleId) return;

  const requestId = ++latestDiscountKitSyncRequestId;
  // Always fetch via the combined POST so we get cart JSON + rendered sections atomically.
  // `cartFromEvent` is preserved as a defensive fallback only — used if the fetch fails.
  const fetched = await fetchLatestCartWithSections();

  if (requestId !== latestDiscountKitSyncRequestId || cycleId !== activeDiscountKitSyncCycleId) return;

  const cart = fetched || cartFromEvent;
  if (!cart) {
    endDiscountKitSyncCycle(cycleId, `${reason}-no-cart`);
    return;
  }

  document.dispatchEvent(
    new CartUpdateEvent(cart, DISCOUNT_KIT_SYNC_SOURCE, {
      itemCount: cart.item_count,
      source: DISCOUNT_KIT_SYNC_SOURCE,
      sections: cart.sections || {},
      reason,
      cycleId,
    })
  );

  endDiscountKitSyncCycle(cycleId, reason);
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const source = event?.detail?.data?.source;
  const itemCount = event?.detail?.data?.itemCount;

  if (event?.detail?.data?.didError || source === DISCOUNT_KIT_SYNC_SOURCE) return;

  const cycleId = startDiscountKitSyncCycle(itemCount);

  discountKitFallbackTimeoutId = window.setTimeout(() => {
    syncCartForCycle(cycleId, null, 'fallback-timeout');
  }, DISCOUNT_KIT_FALLBACK_DELAY_MS);

  document.dispatchEvent(
    new CustomEvent(DISCOUNT_KIT_CART_CHANGED_EVENT, {
      bubbles: true,
      detail: {
        source: 'theme',
        reason: ThemeEvents.cartUpdate,
        itemCount,
        cycleId,
      },
    })
  );
});

document.addEventListener(DISCOUNT_KIT_CART_CHANGED_EVENT, async (event) => {
  if (event instanceof CustomEvent && event.detail?.source === 'theme') return;

  let cycleId =
    event instanceof CustomEvent && event.detail?.cycleId ? event.detail.cycleId : activeDiscountKitSyncCycleId;
  if (!cycleId) cycleId = startDiscountKitSyncCycle(undefined);

  // Stale DK callbacks (old cycleId) must not cancel the active cycle's fallback timer —
  // they'd silently bail out at the cycle-mismatch check inside syncCartForCycle.
  if (cycleId === activeDiscountKitSyncCycleId) {
    clearDiscountKitFallbackTimeout();
  }

  const cartFromEvent = getCartFromDiscountKitEvent(event);
  await syncCartForCycle(cycleId, cartFromEvent, 'app-callback');
});

/**
 * Event class for cart errors
 * @extends {Event}
 */
export class CartErrorEvent extends Event {
  /**
   * Creates a new CartErrorEvent
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {string} message - A message from the server response
   * @param {Object} description - Description from the server response
   * @param {Object} errors - Errors from the server response
   */
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        message,
        errors,
        description,
      },
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine,
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class DiscountUpdateEvent extends Event {
  /**
   * Creates a new DiscountUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   */
  constructor(resource, sourceId) {
    super(ThemeEvents.discountUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
    };
  }
}

/**
 * Event class for media playback starts
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} SlideshowSelectEventData
 * @property {number} index
 * @property {string | null} id
 * @property {Element} slide
 * @property {number} previousIndex
 * @property {boolean} userInitiated
 * @property {'select' | 'scroll' | 'drag'} trigger
 */

export class SlideshowSelectEvent extends Event {
  /**  @param {SlideshowSelectEventData} data */
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true });
    this.detail = data;
  }

  /** @type {SlideshowSelectEventData} */
  detail;

  static eventName = 'slideshow:select';
}

/**
 * Event class for zoom dialog media selection
 * @extends {Event}
 */
export class ZoomMediaSelectedEvent extends Event {
  /**
   * Creates a new ZoomMediaSelectedEvent
   * @param {number} index - The index of the selected media
   */
  constructor(index) {
    super(ThemeEvents.zoomMediaSelected, { bubbles: true });
    this.detail = {
      index,
    };
  }
}

/**
 * Event class for mega menu hover being hovered over
 * @extends {Event}
 */
export class MegaMenuHoverEvent extends Event {
  constructor() {
    super(ThemeEvents.megaMenuHover, { bubbles: true });
  }
}

/** Event class for facet filtering updates */
export class FilterUpdateEvent extends Event {
  /** @param {URLSearchParams} queryParams */
  constructor(queryParams) {
    super(ThemeEvents.FilterUpdate, { bubbles: true });
    this.detail = {
      queryParams,
    };
  }

  shouldShowClearAll() {
    return [...this.detail.queryParams.entries()].filter(([key]) => key.startsWith('filter.')).length > 0;
  }
}
